import React, { useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Upload, Download, CheckCircle, XCircle, AlertCircle, FileSpreadsheet } from 'lucide-react';

export default function BulkImportDialog({ open, onOpenChange, entityType, onSuccess }) {
  const [file, setFile] = useState(null);
  const [previewResults, setPreviewResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const [step, setStep] = useState('upload'); // 'upload', 'preview', 'complete'

  const entityConfig = {
    users: {
      label: 'Users',
      endpoint: '/admin/bulk-import/users',
      color: 'blue'
    },
    vendors: {
      label: 'Vendors',
      endpoint: '/admin/bulk-import/vendors',
      color: 'green'
    },
    drivers: {
      label: 'Drivers',
      endpoint: '/admin/bulk-import/drivers',
      color: 'purple'
    }
  };

  const config = entityConfig[entityType] || entityConfig.users;

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get(`${config.endpoint}/template`);
      
      // Create and download CSV file
      const blob = new Blob([res.data.content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Template downloaded successfully');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setPreviewResults(null);
      setStep('upload');
    } else {
      toast.error('Please select a CSV file');
    }
  };

  const handlePreview = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post(`${config.endpoint}/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setPreviewResults(res.data);
      setStep('preview');
      
      if (res.data.summary.invalid_count > 0 || res.data.summary.duplicate_count > 0) {
        toast.warning(`Found ${res.data.summary.invalid_count + res.data.summary.duplicate_count} issues in CSV`);
      } else {
        toast.success(`${res.data.summary.valid_count} valid entries ready to import`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview import');
    } finally {
      setImporting(false);
    }
  };

  const handleCommit = async () => {
    if (!file) return;

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post(`${config.endpoint}/commit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`Successfully imported ${res.data.created_count} ${config.label.toLowerCase()}`);
      setStep('complete');
      
      // Call parent success callback
      if (onSuccess) {
        onSuccess(res.data);
      }
      
      // Reset after brief delay
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreviewResults(null);
    setStep('upload');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Import {config.label}</DialogTitle>
          <DialogDescription>
            Upload a CSV file to import multiple {config.label.toLowerCase()} at once
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <>
              <Alert>
                <FileSpreadsheet className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  <div className="space-y-2">
                    <p>To bulk import {config.label.toLowerCase()}:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Download the CSV template</li>
                      <li>Fill in your {config.label.toLowerCase()} data</li>
                      <li>Upload the completed CSV file</li>
                    </ol>
                  </div>
                </AlertDescription>
              </Alert>

              <div className="flex gap-2">
                <Button variant="outline" onClick={handleDownloadTemplate} className="flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
              </div>

              <Separator />

              <div className="space-y-2">
                <label className="text-sm font-medium">Upload CSV File</label>
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="flex-1 text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                  />
                  {file && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      {file.name}
                    </Badge>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && previewResults && (
            <>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  <div className="space-y-2">
                    <div className="font-medium">Import Preview</div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-600" />
                        <span>{previewResults.summary.valid_count} Valid</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <XCircle className="h-3 w-3 text-red-600" />
                        <span>{previewResults.summary.invalid_count} Invalid</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <AlertCircle className="h-3 w-3 text-amber-600" />
                        <span>{previewResults.summary.duplicate_count} Duplicates</span>
                      </div>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>

              {/* Show errors if any */}
              {(previewResults.invalid.length > 0 || previewResults.duplicate_emails.length > 0) && (
                <div className="space-y-2 max-h-64 overflow-y-auto border rounded p-3">
                  <div className="text-sm font-medium text-red-600">Issues Found:</div>
                  {previewResults.invalid.map((item, idx) => (
                    <div key={idx} className="text-xs bg-red-50 p-2 rounded">
                      <span className="font-medium">Row {item.row}:</span> {item.error}
                    </div>
                  ))}
                  {previewResults.duplicate_emails.map((item, idx) => (
                    <div key={idx} className="text-xs bg-amber-50 p-2 rounded">
                      <span className="font-medium">Row {item.row}:</span> Duplicate email: {item.email}
                    </div>
                  ))}
                </div>
              )}

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  {previewResults.summary.valid_count > 0
                    ? `Ready to import ${previewResults.summary.valid_count} ${config.label.toLowerCase()}. Invalid/duplicate entries will be skipped.`
                    : 'No valid entries found. Please fix the errors and try again.'}
                </AlertDescription>
              </Alert>
            </>
          )}

          {/* Step 3: Complete */}
          {step === 'complete' && (
            <Alert>
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-sm">
                Import completed successfully! The page will refresh shortly.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={importing}>
            {step === 'complete' ? 'Close' : 'Cancel'}
          </Button>
          
          {step === 'upload' && (
            <Button onClick={handlePreview} disabled={!file || importing}>
              {importing ? 'Processing...' : 'Preview Import'}
            </Button>
          )}
          
          {step === 'preview' && previewResults && previewResults.summary.valid_count > 0 && (
            <Button onClick={handleCommit} disabled={importing}>
              {importing ? 'Importing...' : `Import ${previewResults.summary.valid_count} ${config.label}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
